import { useMemo } from "react";
import { Box, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import type { ForecastReportAnalytics } from "../models/ForecastReport";

const RIGHT_CELL = { textAlign: "right" } as const;
const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const thousandsFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const formatDecimal = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return thousandsFormatter.format(n);
};

const buildColumnDefs = (): ColDef<ForecastReportAnalytics>[] => [
	{
		field: "ProductionYear",
		headerName: "Year",
		width: 70,
		sortable: false,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Mean",
		headerName: "Mean",
		flex: 1,
		minWidth: 90,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "Increase",
		headerName: "YOY Delta",
		flex: 1,
		minWidth: 90,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "StandardDeviation",
		headerName: "Standard Deviation",
		flex: 1,
		minWidth: 120,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "CoefficientOfVariation",
		headerName: "Coeff of Var",
		flex: 1,
		minWidth: 100,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "Range",
		headerName: "Range",
		flex: 1,
		minWidth: 90,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
	{
		field: "PRR",
		headerName: "PRR",
		flex: 1,
		minWidth: 80,
		sortable: false,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatDecimal(p.value),
	},
];

interface Props {
	rows: ForecastReportAnalytics[];
}

export const ForecastReportAnalyticsTable = ({ rows }: Props) => {
	const columnDefs = useMemo(() => buildColumnDefs(), []);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<AgGridReact<ForecastReportAnalytics>
				columnDefs={columnDefs}
				rowData={rows}
				rowModelType="clientSide"
				defaultColDef={{ resizable: true }}
				autoSizeStrategy={{ type: "fitGridWidth" }}
				suppressContextMenu
				headerHeight={32}
				rowHeight={28}
				getRowId={(p) => String(p.data.ProductionYear)}
			/>
		</Box>
	);
};
